import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useNavigate } from 'react-router-dom'

import { FilterMatchMode, FilterOperator } from 'primereact/api'
import { Button } from 'primereact/button'
import { Column } from 'primereact/column'
import { DataTableFilterMeta } from 'primereact/datatable'

import { SearchField } from '../../../../../components/SearchField'
import {
  BooleanBodyTemplate,
  BooleanFilterTemplate,
  DecimalBodyTemplate,
  DecimalFilterTemplate,
  PackagingIdentifierBodyTemplate,
  PackagingIdentifierFilterTemplate,
  PackagingTypeBodyTemplate,
  PackagingTypeFilterTemplate,
  PropertyIdentifierBodyTemplate,
  PropertyIdentifierFilterTemplate,
  VolumeUnitBodyTemplate,
  VolumeUnitFilterTemplate,
  WeightUnitBodyTemplate,
  WeightUnitFilterTemplate,
} from '../../../../../components/tableTemplates'
import { DeliveryForecastDespatchAdvicePackagingMaterial } from '../../../../../data/models'
import { useSearchParam } from '../../../../../hooks'
import { useColumnToggle } from '../../../../../hooks/useColumnToggle'
import { ConfigurableTable } from '../../../../../utils/wrapper'
import { useConfigContext } from '../../../../ui-config'
import { MasterDataDownloadButton } from '../../common/components'
import {
  requestDownloadPackagingMaterial,
  useAllPackagingMaterial,
} from '../data'
import { MasterDataUpload } from './dialogs/Upload'

const defaultFilters: DataTableFilterMeta = {
  global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  PackagingDataId: {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
  },
  Description: {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
  },
  PackagingMaterial: { value: null, matchMode: FilterMatchMode.EQUALS },
  'TareWeight.Value': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
  },
  'TareWeight.Unit': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
  },
  'Volume.Value': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
  },
  'Volume.Unit': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
  },
  PackagingIdentifier: {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
  },
  PropertyIdentifier: { value: null, matchMode: FilterMatchMode.EQUALS },
  IsStackable: { value: null, matchMode: FilterMatchMode.EQUALS },
}

const STORAGE_KEY = 'master-data-packaging-data-visible-columns'
const TOGGLEABLE_FIELDS = [
  'Description',
  'PackagingMaterial',
  'TareWeight.Value',
  'TareWeight.Unit',
  'Volume.Value',
  'Volume.Unit',
  'PackagingIdentifier',
  'PropertyIdentifier',
  'IsStackable',
]

export const PackagingData: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { uiConfig: config } = useConfigContext()
  const { navigationColumnHeader, show } = useColumnToggle(
    config.selfservices.masterDataManagement.packagingData.table,
    TOGGLEABLE_FIELDS,
    STORAGE_KEY,
    true,
  )
  const {
    data: packagingMaterials,
    isLoading,
    error,
  } = useAllPackagingMaterial()
  const [searchParam, setSearchParam] = useSearchParam('search')

  const [selectedItems, setSelectedItems] = useState<any | null>(null)

  const [filters, setFilters] = useState<DataTableFilterMeta>(defaultFilters)

  useEffect(() => {
    // Reset selection when data changes (e.g., after deletion)
    setSelectedItems(null)
  }, [packagingMaterials])

  useEffect(() => {
    initFilters()
  }, [])

  const clearFilter = () => {
    initFilters()
  }

  const initFilters = () => {
    setFilters(defaultFilters)
    setSearchParam('')
  }

  useEffect(() => {
    onGlobalFilterChange(searchParam)
  }, [searchParam])

  const onGlobalFilterChange = (value: string) => {
    let _filters = JSON.parse(JSON.stringify(filters))

    _filters['global'].value = value
    setFilters(_filters)
    setSearchParam(value)
  }

  const handleAdd = () => navigate('add/')

  const headerActions = (
    <>
      <MasterDataDownloadButton
        label={t('masterDataManagement.packagingData.table.download.label')}
        downloadFunction={requestDownloadPackagingMaterial}
        fileNamePrefix="packaging_material"
      />

      <MasterDataUpload />

      <Button
        label={t('masterDataManagement.packagingData.table.delete.label')}
        onClick={() => handleDelete(selectedItems.map((item: any) => item.Id))}
        icon="pi pi-trash"
        outlined
        disabled={!selectedItems?.length}
      />

      <Button
        label={t('masterDataManagement.packagingData.table.add.label')}
        onClick={() => handleAdd()}
        icon="pi pi-plus"
        visible={
          config.selfservices.masterDataManagement.packagingData.add.enabled !==
          false
        }
      />

      <SearchField
        searchValue={searchParam}
        setSearchValue={setSearchParam}
        placeholder={t(
          'masterDataManagement.packagingData.table.search.placeholder',
        )}
        classNameInput="surface-50"
      />
    </>
  )

  //  action template
  const handleEdit = (packagingId: string) => navigate('edit/' + packagingId)
  const handleInfo = (packagingId: string) => navigate('info/' + packagingId)
  const handleDelete = (packagingIds: string[]) =>
    navigate('delete/' + packagingIds.join(','))

  const actionBodyTemplate = (
    rowData: DeliveryForecastDespatchAdvicePackagingMaterial,
  ) => {
    return (
      <div className="flex justify-content-end gap-2 flex-nowrap">
        <Button
          text
          icon="pi pi-trash"
          severity="danger"
          rounded
          aria-label={t(
            'masterDataManagement.packagingData.table.delete.label',
          )}
          onClick={() => handleDelete([rowData?.Id || ''])}
          tooltip={t(
            'masterDataManagement.companyCodes.table.delete.tooltip.isStandard',
          )}
        />

        <Button
          text
          icon="pi pi-info-circle"
          rounded
          aria-label={t('masterDataManagement.packagingData.table.info.label')}
          onClick={() => handleInfo(rowData?.Id || '')}
          visible={
            config.selfservices.masterDataManagement.packagingData.info
              .enabled !== false
          }
        />
        <Button
          text
          icon="pi pi-pencil"
          rounded
          aria-label={t('masterDataManagement.packagingData.table.edit.label')}
          onClick={() => handleEdit(rowData?.Id || '')}
          visible={
            config.selfservices.masterDataManagement.packagingData.edit
              .enabled !== false
          }
        />
      </div>
    )
  }

  const buttons = (
    <Column
      key="buttons"
      body={actionBodyTemplate}
      align="right"
      header={
        <div className="flex justify-content-end">{navigationColumnHeader}</div>
      }
    />
  )

  return (
    <>
      <ConfigurableTable
        config={config.selfservices.masterDataManagement.packagingData.table}
        data={packagingMaterials}
        headerActions={headerActions}
        defaultFilters={filters}
        updateDefaultFilters={setFilters}
        clearDefaultFilter={clearFilter}
        globalFilterFields={['PackagingDataId', 'Description']}
        globalFilterValue={searchParam}
        setGlobalFilterValue={setSearchParam}
        isLoading={isLoading}
        stateKey="table-master-data-management-packaging-data"
        buttons={buttons}
        checkboxen
        selected={selectedItems}
        setSelected={setSelectedItems}
      >
        {[
          <Column
            key="PackagingDataId"
            field="PackagingDataId"
            filter
            sortable
          />,
          show('Description') ? (
            <Column key="Description" field="Description" filter sortable />
          ) : null,
          show('PackagingMaterial') ? (
            <Column
              key="PackagingMaterial"
              field="PackagingMaterial"
              body={(
                rowData: DeliveryForecastDespatchAdvicePackagingMaterial,
              ) => PackagingTypeBodyTemplate(rowData.PackagingMaterial)}
              filter
              filterElement={PackagingTypeFilterTemplate}
              sortable
            />
          ) : null,
          show('TareWeight.Value') ? (
            <Column
              key="TareWeight.Value"
              field="TareWeight.Value"
              body={(
                rowData: DeliveryForecastDespatchAdvicePackagingMaterial,
              ) => DecimalBodyTemplate(rowData.TareWeight?.Value)}
              dataType="numeric"
              filter
              filterElement={DecimalFilterTemplate}
              sortable
            />
          ) : null,
          show('TareWeight.Unit') ? (
            <Column
              key="TareWeight.Unit"
              field="TareWeight.Unit"
              body={(
                rowData: DeliveryForecastDespatchAdvicePackagingMaterial,
              ) => WeightUnitBodyTemplate(rowData.TareWeight?.Unit)}
              filterElement={WeightUnitFilterTemplate}
            />
          ) : null,
          show('Volume.Value') ? (
            <Column
              key="Volume.Value"
              field="Volume.Value"
              body={(
                rowData: DeliveryForecastDespatchAdvicePackagingMaterial,
              ) => DecimalBodyTemplate(rowData.Volume?.Value)}
              dataType="numeric"
              filter
              filterElement={DecimalFilterTemplate}
              sortable
            />
          ) : null,
          show('Volume.Unit') ? (
            <Column
              key="Volume.Unit"
              field="Volume.Unit"
              body={(
                rowData: DeliveryForecastDespatchAdvicePackagingMaterial,
              ) => VolumeUnitBodyTemplate(rowData.Volume?.Unit)}
              filterElement={VolumeUnitFilterTemplate}
            />
          ) : null,
          show('PackagingIdentifier') ? (
            <Column
              key="PackagingIdentifier"
              field="PackagingIdentifier"
              body={(
                rowData: DeliveryForecastDespatchAdvicePackagingMaterial,
              ) => PackagingIdentifierBodyTemplate(rowData.PackagingIdentifier)}
              filter
              filterElement={PackagingIdentifierFilterTemplate}
              sortable
            />
          ) : null,
          show('PropertyIdentifier') ? (
            <Column
              key="PropertyIdentifier"
              field="PropertyIdentifier"
              body={(
                rowData: DeliveryForecastDespatchAdvicePackagingMaterial,
              ) => PropertyIdentifierBodyTemplate(rowData.PropertyIdentifier)}
              filter
              filterElement={PropertyIdentifierFilterTemplate}
              sortable
            />
          ) : null,
          show('IsStackable') ? (
            <Column
              key="IsStackable"
              field="IsStackable"
              body={(
                rowData: DeliveryForecastDespatchAdvicePackagingMaterial,
              ) => BooleanBodyTemplate(rowData.IsStackable)}
              filter
              filterElement={BooleanFilterTemplate}
              sortable
            />
          ) : null,
        ].filter((c): c is JSX.Element => c !== null)}
      </ConfigurableTable>

      <Outlet />
    </>
  )
}
